import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import MenuShell from '../components/MenuShell';
import { bulkImportRoster } from '../features/admin/adminImportApi';
import type { BulkImportResponse, BulkImportRow } from '../features/admin/adminImportApi';
import { ApiError } from '../services/apiClient';
import { parseCsv } from '../utils/csv';

const REQUIRED_HEADERS = ['email', 'password', 'groupe'] as const;

function AdminImportPage() {
  const { t } = useTranslation();
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<BulkImportRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<BulkImportResponse | null>(null);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setResult(null);
    setSubmitError(null);
    setRows([]);
    setFileName(null);
    if (!file) {
      return;
    }

    const text = await file.text();
    const parsed = parseCsv(text);
    const headers = parsed[0] ? Object.keys(parsed[0]) : [];
    const missingHeaders = REQUIRED_HEADERS.filter((header) => !headers.includes(header));

    if (missingHeaders.length > 0) {
      setParseError(t('adminImport.missingHeaders', { headers: missingHeaders.join(', ') }));
      return;
    }

    setParseError(null);
    setFileName(file.name);
    setRows(parsed.map((row) => ({
      email: row.email ?? '',
      password: row.password ?? '',
      groupe: row.groupe ?? '',
      modele: row.modele ?? '',
      nom: row.nom ?? '',
      prenom: row.prenom ?? '',
    })));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setResult(null);
    try {
      const response = await bulkImportRoster(rows);
      setResult(response);
    } catch (error) {
      setSubmitError(error instanceof ApiError ? error.message : t('adminImport.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <MenuShell courseId="" />
      <main style={{ maxWidth: 720, margin: '3rem auto', padding: '0 1rem', display: 'grid', gap: '1.5rem' }}>
        <h1>{t('adminImport.title')}</h1>
        <p>{t('adminImport.tagline')}</p>

        <label>
          {t('adminImport.fileLabel')}
          <input type="file" accept=".csv,text/csv" onChange={handleFileChange} />
        </label>

        {parseError && <p role="alert">{parseError}</p>}

        {fileName && !parseError && (
          <p role="status">{t('adminImport.parsedSummary', { fileName, count: rows.length })}</p>
        )}

        {rows.length > 0 && (
          <button type="button" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? t('adminImport.submitting') : t('adminImport.submit')}
          </button>
        )}

        {submitError && <p role="alert">{submitError}</p>}

        {result && (
          <section>
            <p role="status">
              {t('adminImport.resultSummary', {
                created: result.createdCount,
                enrolled: result.enrolledCount,
                errors: result.errorCount,
              })}
            </p>
            <table>
              <thead>
                <tr>
                  <th>{t('adminImport.columnRow')}</th>
                  <th>{t('adminImport.columnEmail')}</th>
                  <th>{t('adminImport.columnGroupe')}</th>
                  <th>{t('adminImport.columnStatus')}</th>
                  <th>{t('adminImport.columnMessage')}</th>
                </tr>
              </thead>
              <tbody>
                {result.results.map((rowResult) => (
                  <tr key={rowResult.row}>
                    <td>{rowResult.row}</td>
                    <td>{rowResult.email}</td>
                    <td>{rowResult.groupe}</td>
                    <td>{t(`adminImport.status.${rowResult.status}`)}</td>
                    <td>{rowResult.message ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </main>
    </>
  );
}

export default AdminImportPage;
